import MojitoCore from 'mojito/core';

import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';

import {constructTilePalette} from '#core/application/modules/tiles-cache.js';
import {generateToggleButton} from '#core/application/modules/tiles/button/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generateEventsCoupon} from '#core/application/modules/tiles/events-coupon/index.js';
import {generatePivotsRow} from '#core/application/modules/tiles/pivot/index.js';
import {generatePriceBoostItem} from '#core/application/modules/tiles/price-boost-item/index.js';
import {generateEventListOffers} from '#core/application/modules/tiles/event-list/index.js';
import {generateNeutralVenueIndicator} from '#core/application/modules/tiles/neutral-venue-indicator/index.js';
import {generateEventTimingStatus} from '#core/application/modules/tiles/event-timing-status/index.js';

const merge = MojitoCore.Presentation.uiStyle.merge;

export function couponsFactory(PALETTE) {
    const {FONT} = PALETTE;

    // we need to set these styles for some coupons out of coupons generator because by Zeplin some of them should have
    // round corners with their siblings, as example see how TopSports are designed in Zeplin
    const COUPONS_COMMON_STYLES = {
        ...(PALETTE.COUPONS_SHOW_SHADOW ? {boxShadow: PALETTE.COUPONS_BOX_SHADOW} : {}),
        borderRadius: 8,
        overflow: 'hidden',
    };
    const IN_PLAY_TAB_COUPON = {
        eventsCoupon: merge(generateEventsCoupon(PALETTE.IN_PLAY_COUPON), {
            container: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                },
            },
        }),
    };
    const SPORT_PAGE_COUPON = {
        eventCoupon: merge(generateEventsCoupon(PALETTE.SPORT_COUPON), {
            initialExpandedGroups: 10,
            container: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                    _mobile_margin: '8px 8px 0', // unable to set padding here because page doesn't have container config
                },
            },
            eventList: {
                header: {
                    _desktop_showTitle: true,
                },
            },
            eventListSkeleton: {
                _desktop_showEventListTitle: true,
            },
        }),
    };

    // needed only for scroll buttons on desktop, doesn't require separate token in palette
    const TOP_SPORTS_MENU_ROW = generatePivotsRow(
        constructTilePalette('PIVOT', null, PALETTE.TOP_SPORTS_SPORT_SELECTOR_CONTAINER_BG)
    );

    return {
        PrebuiltBets: {
            container: {
                style: GENBET_BASED ? {flexDirection: 'column'} : {display: 'none'},
            },
            carouselTitle: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_SEMI_BOLD,
                    textTransform: 'uppercase',
                    ...PALETTE.COUPONS_TITLE_TEXT,
                },
            },
            swiper: {
                style: {
                    scrollPaddingLeft: '8px',
                    scrollPaddingRight: '8px',
                },
            },
        },
        PrebuiltBetBuilderCoupon: {
            eventNameLabel: {
                style: PALETTE.PBB_COUPON_EVENT_NAME_TEXT,
            },
            eventTimingStatus: generateEventTimingStatus(PALETTE.PBB_COUPON_TIMING_STATUS),
            container: {
                style: {
                    maxWidth: '100%', // todo remove in 25.7 MOJ-15428
                },
            },
            skeletonLoader: {
                style: {
                    container: {
                        maxWidth: '100%',
                    },
                },
            },
        },
        PrebuiltBetBuilderView: {
            navigationMenu: {
                item: generateToggleButton(PALETTE.PBB_COUPON_SELECTOR, true, {
                    WITH_ACTIVE: false,
                    WITH_HOVER: false,
                }),
                itemIcon: icon(PALETTE.PBB_COUPON_SELECTOR.TOGGLE_BUTTON_ICON_COLOR),
                selectedItemIcon: icon(PALETTE.PBB_COUPON_SELECTOR.TOGGLE_BUTTON_SELECTED_ICON_COLOR),
                buttonScrollPane: TOP_SPORTS_MENU_ROW.buttonScrollPane,
            },
            menuRowSkeleton: {
                itemContainer: generateSkeletonPart(PALETTE.PBB_NAVIGATION_MENU_SKELETON),
            },
        },
        TopSports: {
            // TODO: move to feature config
            showMenuSportIcon: true,
            showCouponSportIcon: true,
            style: {
                navigationTitle: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    textTransform: 'uppercase',
                    padding: '8px',
                    _mobile_padding: 8,
                    _mobile_textAlign: 'left',
                    ...PALETTE.COUPONS_TITLE_TEXT,
                },
            },
            mainContainer: {
                style: {
                    _mobile_padding: '0 8px',
                },
            },
            couponContainer: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                },
            },
            navigationMenuContainer: {
                style: {
                    ...resolveBackground(PALETTE.TOP_SPORTS_SPORT_SELECTOR_CONTAINER_BG),
                    maxWidth: '100%',
                    borderWidth: '0 0 1px',
                    borderStyle: 'solid',
                    borderColor: PALETTE.TOP_SPORTS_SPORT_SELECTOR_CONTAINER_SEPARATOR_COLOR,
                },
            },
            navigationMenu: {
                item: generateToggleButton(PALETTE.TOP_SPORTS_SPORT_SELECTOR, true, {
                    WITH_ACTIVE: false,
                    WITH_HOVER: false,
                }),
                itemIcon: icon(PALETTE.TOP_SPORTS_SPORT_SELECTOR.TOGGLE_BUTTON_ICON_COLOR),
                selectedItemIcon: icon(PALETTE.TOP_SPORTS_SPORT_SELECTOR.TOGGLE_BUTTON_SELECTED_ICON_COLOR),
                buttonScrollPane: TOP_SPORTS_MENU_ROW.buttonScrollPane,
            },
            navigationMenuSkeleton: {
                numberOfItems: 5,
                _mobile_numberOfItems: 4,
                container: {
                    style: {
                        ...resolveBackground(PALETTE.TOP_SPORTS_SPORT_SELECTOR_CONTAINER_BG),
                        boxSizing: 'border-box',
                        width: '100%',
                    },
                },
                iconPlaceholder: generateSkeletonPart(PALETTE.TOP_SPORTS_SKELETON_PART),
                linkPlaceholder: merge(generateSkeletonPart(PALETTE.TOP_SPORTS_SKELETON_PART), {
                    style: {
                        height: '40px',
                        borderRadius: PALETTE.TOP_SPORTS_SPORT_SELECTOR.TOGGLE_BUTTON_BORDER_RADIUS,
                    },
                }),
            },
            eventsCoupon: generateEventsCoupon(PALETTE.TOP_SPORTS_COUPON),
            // top sports skeleton
            skeletonLoader: {
                style: {
                    container: {
                        _mobile_justifyContent: 'flex-start',
                    },
                },
            },
        },
        InPlayContent: {
            container: {
                itemSpacing: 8,
            },
            allTab: merge(IN_PLAY_TAB_COUPON, {
                eventsCoupon: {
                    eventList: {
                        header: {
                            showMarketSelectors: true,
                        },
                    },
                },
            }),
            sportTab: IN_PLAY_TAB_COUPON,
            streamingTab: IN_PLAY_TAB_COUPON,
            sportTitle: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    textTransform: 'uppercase',
                    _mobile_textAlign: 'left',
                    ...PALETTE.COUPONS_TITLE_TEXT,
                },
            },
        },
        SingleLeagueCoupon: {
            container: {
                style: {
                    _mobile_padding: 8,
                },
            },
            eventsCoupon: merge(generateEventsCoupon(PALETTE.SINGLE_LEAGUE_COUPON), {
                container: {
                    style: {
                        ...COUPONS_COMMON_STYLES,
                        _mobile_padding: 0,
                    },
                },
                eventList: {
                    header: {
                        container: {
                            style: {
                                borderRadius: '8px 8px 0 0',
                            },
                        },
                    },
                    eventListGroup: {
                        container: {
                            style: {
                                borderRadius: '0 0 8px 8px',
                            },
                        },
                        offersContainer: {
                            style: {
                                padding: 8,
                            },
                        },
                    },
                },
            }),
        },
        // we need this config for EarlyPayoutOffers only to style it inside event list header in SingleLeagueCoupon
        // because they didn't provide us with nested configuration for EPO inside event list header component (Mojito 🤌)
        EarlyPayoutOffers: generateEventListOffers(PALETTE.SINGLE_LEAGUE_COUPON.EVENTS_COUPON_EVENT_LIST),
        // Sport Coupon pages
        SportCouponView: SPORT_PAGE_COUPON,
        CmsSportCouponView: SPORT_PAGE_COUPON,
        // Matches
        MatchesView: {
            container: {
                style: {
                    padding: '8px 0 0 0',
                    _mobile_padding: '8px 8px 0',
                },
            },
            contentContainer: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                },
            },
            filterContainer: {
                style: {
                    ...resolveBackground(PALETTE.MATCHES_FILTER_BG),
                },
            },
            filtersBar: {
                defaultNamingSchemaForDayFilters: GENBET_BASED ? 'EEE d' : 'EEEE',
                filter: {
                    container: {
                        style: {
                            backgroundColor: 'transparent',
                        },
                    },
                    button: generateToggleButton(PALETTE.MATCHES_FILTER_BUTTON, true),
                },
                topMenuSkeleton: {
                    buttonSkeleton: generateSkeletonPart({
                        ...PALETTE.MATCHES_FILTER_BUTTON_SKELETON,
                        SKELETON_BORDER_RADIUS: PALETTE.MATCHES_FILTER_BUTTON.TOGGLE_BUTTON_BORDER_RADIUS,
                    }),
                },
            },
        },
        MatchesCoupon: {
            eventsCoupon: {
                ...generateEventsCoupon(PALETTE.MATCHES_COUPON),
                eventListSkeleton: {
                    _desktop_showEventListTitle: false,
                    eventListHeaderContainer: {
                        style: {
                            _desktop_justifyContent: 'end',
                        },
                    },
                },
            },
        },
        // PriceBoost
        PriceBoostsView: {
            container: {
                style: {
                    backgroundColor: 'transparent',
                    margin: '8px 0 0',
                    _mobile_margin: 0,
                },
            },
            navigationMenu: {
                item: generateToggleButton(PALETTE.PRICE_BOOST_COUPON_SELECTOR, true, {
                    WITH_ACTIVE: false,
                    WITH_HOVER: false,
                }),
                itemIcon: icon(PALETTE.PRICE_BOOST_COUPON_SELECTOR.TOGGLE_BUTTON_ICON_COLOR),
                selectedItemIcon: icon(PALETTE.PRICE_BOOST_COUPON_SELECTOR.TOGGLE_BUTTON_SELECTED_ICON_COLOR),
                buttonScrollPane: TOP_SPORTS_MENU_ROW.buttonScrollPane,
                menuRowSkeleton: {
                    itemContainer: generateSkeletonPart(PALETTE.PRICE_BOOST_NAVIGATION_MENU_SKELETON),
                },
            },
        },
        PriceBoostCoupon: {
            container: {
                style: {
                    padding: 0,
                    _mobile_padding: 8,
                },
                separator: {
                    style: {
                        size: 1,
                        color: PALETTE.PRICE_BOOST_COUPON_EVENT_SEPARATOR_COLOR,
                    },
                },
            },
            sportNameTitle: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    textTransform: 'uppercase',
                    _mobile_textAlign: 'left',
                    ...PALETTE.COUPONS_TITLE_TEXT,
                },
            },
            eventContainer: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                    ...resolveBackground(PALETTE.PRICE_BOOST_COUPON_EVENTS_BG),
                    padding: 0,
                },
            },
            eventHeaderContainer: {
                style: {
                    padding: '0 8px',
                },
            },
            eventTimingStatus: generateEventTimingStatus(PALETTE.PRICE_BOOST_COUPON_TIMING_STATUS),
            neutralVenueIndicator: generateNeutralVenueIndicator(PALETTE.PRICE_BOOST_COUPON_NEUTRAL_VENUE_INDICATOR),
            eventNameLabel: {
                container: {
                    padding: '2px 0',
                },
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    fontWeight: FONT.WEIGHT_SEMI_BOLD,
                    ...PALETTE.PRICE_BOOST_COUPON_EVENT_NAME_TEXT,
                },
            },
            boostedMarketContent: {
                priceBoostItem: merge(generatePriceBoostItem(PALETTE.PRICE_BOOST_COUPON_ITEM), {
                    container: {
                        style: {
                            padding: 8,
                        },
                    },
                }),
            },
        },
        PriceBoostMarketContent: {
            container: {
                separator: {
                    style: {
                        size: 1,
                        color: PALETTE.PRICE_BOOST_COUPON_EVENT_SEPARATOR_COLOR,
                    },
                },
            },
        },
        PriceBoostCouponSkeleton: {
            container: {
                style: {
                    ...COUPONS_COMMON_STYLES,
                    ...resolveBackground(PALETTE.PRICE_BOOST_COUPON_BG),
                },
            },
            marketGroupContainer: {
                separator: {
                    style: {
                        color: PALETTE.PRICE_BOOST_COUPON_EVENT_SEPARATOR_COLOR,
                    },
                },
            },
            startTime: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
            eventNameLabel: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
            selection: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
            selectionLabel: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
            marketLabel: generateSkeletonPart(PALETTE.PRICE_BOOST_COUPON_SKELETON),
        },
    };
}
